/**
 * `UpsellInline` — the small paywall pill consumers render when
 * `useEntitlement()` returns `allowed: false`.
 *
 * Design intent: render *in place of* the action control the user
 * would otherwise click. Not a modal. Not a toast. Three degrees:
 *
 *   - `variant="button"` — a primary-colour button that takes the
 *     footprint of the original CTA and routes to billing. Pair with
 *     a disabled shadow of the real action if the consumer wants to
 *     show the form next to it.
 *   - `variant="badge"` — a compact crown + "Pro" badge, for use
 *     beside radio options (e.g. "Hourly" schedule when the user is
 *     on Indie).
 *   - `variant="card"` — a bordered card that fills the container,
 *     for use when the whole feature area is gated (Webhooks tab on
 *     an unpaid workspace).
 *
 * Kept intentionally tiny — the `UpsellDialog` still fires on
 * server-side 402s and covers the "mid-action" upsell. `UpsellInline`
 * is pre-action: the user never tries to submit, they see the lock.
 *
 * Accessibility:
 *   - Every variant links to `/settings/billing`.
 *   - Button + card render a real `<a>` via Base UI's `render` prop.
 *   - Badge is a `<span>` + tooltip-friendly `title` because it's
 *     used as an adornment (e.g. inside a SelectItem) where an
 *     anchor would escape the select.
 */
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Crown, Lock, Sparkle } from '@phosphor-icons/react';
import { Link } from '@tanstack/react-router';
import type { ReactElement, ReactNode } from 'react';

export type UpsellVariant = 'button' | 'badge' | 'card';

export interface UpsellInlineProps {
  /**
   * Short capability label shown in the copy ("Upgrade to unlock
   * hourly monitors"). If omitted we fall back to the generic
   * "this feature".
   */
  label?: string;
  /**
   * Required plan slug — shown in the button text / tooltip
   * ("Upgrade to Pro"). Accepts any lowercase slug; we title-case
   * for display.
   */
  minPlan: string;
  variant?: UpsellVariant;
  /** Optional extra CSS — merged onto the root element. */
  className?: string;
  /**
   * Override description copy on the `card` variant. Ignored for
   * button / badge.
   */
  description?: ReactNode;
}

function prettyPlan(slug: string): string {
  if (!slug) return 'a paid plan';
  return slug.charAt(0).toUpperCase() + slug.slice(1);
}

export function UpsellInline(props: UpsellInlineProps): ReactElement {
  const variant = props.variant ?? 'button';
  const planLabel = prettyPlan(props.minPlan);
  const featureLabel = props.label ?? 'this feature';

  if (variant === 'badge') {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded-full bg-[var(--color-accent-brand)]/10 px-2 py-0.5 text-xs font-medium text-[var(--color-accent-brand)]',
          props.className,
        )}
        title={`${planLabel} plan required to unlock ${featureLabel}`}
      >
        <Crown aria-hidden="true" size={12} weight="light" />
        {planLabel}
      </span>
    );
  }

  if (variant === 'card') {
    return (
      <section
        className={cn(
          'flex flex-col items-start gap-3 rounded-2xl border border-[var(--color-warning)]/40 bg-[var(--color-warning)]/10 p-5 sm:flex-row sm:items-center sm:justify-between',
          props.className,
        )}
        data-slot="upsell-inline-card"
      >
        <div className="flex items-start gap-3">
          <Lock
            aria-hidden="true"
            size={24}
            weight="light"
            className="mt-0.5 shrink-0 text-[var(--color-mute)]/80"
          />
          <div>
            <h3 className="text-sm font-semibold text-[var(--color-ink)]">
              {planLabel} plan required
            </h3>
            <p className="mt-1 text-sm text-[var(--color-mute)]">
              {props.description ??
                `Upgrade to ${planLabel} to unlock ${featureLabel} for this workspace.`}
            </p>
          </div>
        </div>
        <Button size="sm" render={<Link to="/settings/billing" />}>
          <Sparkle aria-hidden="true" />
          View plans
        </Button>
      </section>
    );
  }

  // Default: 'button'.
  return (
    <Button
      className={props.className}
      render={<Link to="/settings/billing" />}
      data-slot="upsell-inline-button"
    >
      <Sparkle aria-hidden="true" />
      Upgrade to {planLabel}
    </Button>
  );
}
